import Cookies from 'js-cookie';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import { useAppSelector } from '../../hooks';
import { ResourceDetails } from '../../lib/types';
import { appSelectors } from '../../redux/appSlice';
import UsageConsentForm from '../UsageConsentForm';
import { postAnalyticsEvent } from '../../lib/analytics';

type ResourceModalProps = {
  resourceData: ResourceDetails;
  resourceId?: number;
  isLoading: boolean;
};

export const ResourceDetailsContent = ({
  resourceData,
  resourceId,
  isLoading,
}: ResourceModalProps) => {
  const [, setSearchParams] = useSearchParams();
  const isLoggedIn = useAppSelector(appSelectors.isLoggedIn);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [heldImage, setHeldImage] = useState<string | undefined>(undefined);

  const onSubmit = () => {
    heldImage && startDownload(heldImage);
    setHeldImage(undefined);
    setShowForm(false);
  };

  const startDownload = useCallback(
    (imageUrl: string) => {
      window.open(imageUrl, '_blank')?.focus();
      if (resourceId) {
        postAnalyticsEvent('image_library_download_resource', { resourceIds: [resourceId], imagesPerDownload: 1 });
      }
    },
    [resourceId]
  );

  const handleDownloadClick = (imageUrl: string) => {
    const usage_consent = Cookies.get('image_library_usage_consent');
    if (usage_consent === '1' || isLoggedIn) {
      startDownload(imageUrl);
    } else {
      setShowForm(true);
      setHeldImage(imageUrl);
    }
  };

  const handleAddToCollectionClick = () => {
    setSearchParams((currentSearchParams: URLSearchParams): URLSearchParams => {
      currentSearchParams.set('add', 'true');
      return currentSearchParams;
    });
  };

  const topics = useMemo<string[]>(() => {
    return (
      resourceData?.metadata.areaofwork.value
        .split(', ')
        .filter((topic: string): boolean => topic.length > 0) || []
    );
  }, [resourceData?.metadata.areaofwork]);

  const people = useMemo<string[]>(() => {
    return (
      resourceData?.metadata.people.value
        .split(', ')
        .filter((person: string): boolean => person.length > 0) || []
    );
  }, [resourceData?.metadata.people]);

  return (
    <div className="resource-details-content">
      {resourceData && (
        <>
          <div className="resource-details-header">
            <img src={resourceData.images.preview} />
          </div>
          {!showForm && (
            <div className="resource-details-wrapper">
              <div className="category-wrapper">
                {topics.length > 0 && (
                  <div className="category-topic">
                    <div className="category-title">Tags</div>
                    <div className="category-values">
                      <ul>
                        {[...topics, ...people].map((tag: string, index) => {
                          return <li key={tag + index}>{tag}</li>;
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              {resourceData.metadata.credit?.value && (
                <div className="credit">
                  Credit: {resourceData.metadata.credit.value}
                </div>
              )}
              {resourceData.metadata.notes?.value && (
                <div className="notes">{resourceData.metadata.notes.value}</div>
              )}
              {isLoggedIn && (
                <button onClick={handleAddToCollectionClick}>
                  Add to collection
                </button>
              )}
              <table>
                <thead>
                  <tr>
                    <th>File information</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Original File</td>
                    <td>
                      <button
                        onClick={() =>
                          handleDownloadClick(resourceData.images.original)
                        }
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td>Ideal for PowerPoint Presentations</td>
                    <td>
                      <button
                        onClick={() =>
                          handleDownloadClick(resourceData.images.powerpoint)
                        }
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td>Preview</td>
                    <td>
                      <button
                        onClick={() =>
                          handleDownloadClick(resourceData.images.preview)
                        }
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {showForm && !isLoggedIn && <UsageConsentForm onSuccess={onSubmit} />}
      {isLoading && <BeatLoader color="#000000" />}
    </div>
  );
};
